import { useState } from "react";
import {
  useForm,
  useFieldArray,
  useWatch,
  FormProvider,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, Pencil, Plus, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import PhoneInput from "react-phone-number-input/input";
import "react-phone-number-input/style.css";
import { PriceSummary } from "@/modules/eventRegistration/features/EventRegistration/components/PriceSummary/PriceSummary";
import { useRegistration } from "@/modules/eventRegistration/features/EventRegistration/context/RegistrationContext";
import { useNotification } from "@/context/NotificationContext";
import AddAttendees from "@/modules/eventRegistration/features/EventRegistration/components/AddAttendees/AddAttendees";
import {
  contactFormSchema,
  type ContactFormData,
  type AttendeesFormData,
} from "@/modules/eventRegistration/features/EventRegistration/types/formTypes";
import {
  computeIsWaitlist,
  computeWaitlistedCount,
  computeConfirmedCount,
} from "@/modules/eventRegistration/features/EventRegistration/utils/attendeeWaitlist";
import { detectDuplicateEmails } from "@/modules/eventRegistration/features/EventRegistration/utils/detectDuplicateEmails";
import { normalizeEmail } from "@/modules/eventRegistration/features/EventRegistration/utils/normalizeEmail";

export function ReviewStep() {
  const { showNotification } = useNotification();
  const {
    contactName,
    contactEmail,
    contactPhone,
    attendees,
    groupNotes,
    eventInfo,
    updateContact,
    setAttendees,
    nextStep,
  } = useRegistration();

  const [notes, setNotes] = useState(groupNotes);
  const [editingContact, setEditingContact] = useState(false);
  const [editingAttendees, setEditingAttendees] = useState(false);

  const eventPrice = eventInfo?.eventPrice ?? 0;
  const availableSeats = eventInfo?.availableSeats ?? 0;
  const isCoveredByTotalAccess = eventInfo?.isCoveredByTotalAccess ?? false;
  const selectedAttendees = attendees.filter((a) => a.isSelected);
  const confirmedCount = computeConfirmedCount(
    selectedAttendees.length,
    availableSeats,
  );
  const waitlistedCount = computeWaitlistedCount(
    selectedAttendees.length,
    availableSeats,
  );

  // Contact form
  const contactForm = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      contactName,
      contactEmail,
      contactPhone,
    },
    mode: "onChange",
  });

  // Attendees form
  const attendeesForm = useForm<AttendeesFormData>({
    defaultValues: {
      attendees: attendees.map((a) => ({
        firstName: a.firstName,
        lastName: a.lastName,
        email: a.email,
        isSelected: a.isSelected,
        specialRequests: a.specialRequests,
      })),
    },
    mode: "onChange",
  });

  const { fields, remove, append } = useFieldArray({
    control: attendeesForm.control,
    name: "attendees",
  });

  const watchedAttendees =
    useWatch({
      control: attendeesForm.control,
      name: "attendees",
    }) || [];

  const [duplicateEmailError, setDuplicateEmailError] = useState<string | null>(
    null,
  );

  // Special requests state
  const [specialRequests, setSpecialRequests] = useState<
    Record<number, string>
  >(() => {
    const initial: Record<number, string> = {};
    attendees.forEach((a, i) => {
      if (a.specialRequests) initial[i] = a.specialRequests;
    });
    return initial;
  });

  const [expandedRequests, setExpandedRequests] = useState<Set<number>>(() => {
    const initial = new Set<number>();
    attendees.forEach((a, i) => {
      if (a.specialRequests) initial.add(i);
    });
    return initial;
  });

  function handleSpecialRequestChange(index: number, value: string) {
    setSpecialRequests((prev) => ({ ...prev, [index]: value }));
  }

  function toggleSpecialRequests(index: number) {
    setExpandedRequests((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  // Save contact inline
  async function handleSaveContact() {
    const valid = await contactForm.trigger();
    if (!valid) return;
    const data = contactForm.getValues();
    updateContact({
      contactName: data.contactName,
      contactEmail: data.contactEmail,
      contactPhone: data.contactPhone || "",
    });
    setEditingContact(false);
  }

  // Save attendees inline
  function handleSaveAttendees() {
    const data = attendeesForm.getValues();

    const duplicateEmails = detectDuplicateEmails(data.attendees);

    if (duplicateEmails.length > 0) {
      showNotification(
        "Duplicate Emails Detected",
        `Duplicate email${duplicateEmails.length > 1 ? "s" : ""} found: ${duplicateEmails.join(", ")}. Please ensure all attendees have unique email addresses.`,
        "error",
      );
      return;
    }

    const selected = data.attendees.filter((a) => a.isSelected);
    const mapped = selected.map((a, index) => ({
      firstName: a.firstName,
      lastName: a.lastName,
      email: a.email,
      isSelected: true,
      isWaitlist: computeIsWaitlist(index, availableSeats),
      specialRequests: a.specialRequests,
    }));

    const unselected = data.attendees
      .filter((a) => !a.isSelected)
      .map((a) => ({
        firstName: a.firstName,
        lastName: a.lastName,
        email: a.email,
        isSelected: false,
        isWaitlist: false,
        specialRequests: a.specialRequests,
      }));

    setAttendees([...mapped, ...unselected]);
    setEditingAttendees(false);
  }

  function handleAddAttendee(attendee: {
    firstName: string;
    lastName: string;
    email?: string;
  }) {
    if (attendee.email) {
      const normalizedEmail = normalizeEmail(attendee.email);
      const isDuplicate = fields.some(
        (field) =>
          field.email && normalizeEmail(field.email) === normalizedEmail,
      );
      if (isDuplicate) {
        setDuplicateEmailError(
          `An attendee with email ${attendee.email} has already been added.`,
        );
        return;
      }
    }
    setDuplicateEmailError(null);
    append({
      ...attendee,
      isSelected: true,
    });
  }

  function handleContinue() {
    // Validate contact info — name, email, and phone are all required.
    if (!contactName || !contactEmail || !contactPhone) {
      showNotification(
        "Missing Contact Info",
        "Please fill in contact name, email, and phone.",
        "error",
      );
      setEditingContact(true);
      return;
    }

    // Validate attendees
    if (selectedAttendees.length === 0) {
      showNotification(
        "No Attendees",
        "Please add at least one attendee.",
        "error",
      );
      setEditingAttendees(true);
      return;
    }

    updateContact({ groupNotes: notes });

    // Save special requests back to attendees
    const updatedAttendees = attendees.map((a, i) => ({
      ...a,
      specialRequests: specialRequests[i] || "",
    }));
    setAttendees(updatedAttendees);

    nextStep();
  }

  const selectedAttendeeCount = watchedAttendees.filter(
    (a) => a?.isSelected,
  ).length;
  const editWaitlistedCount = Math.max(
    0,
    selectedAttendeeCount - availableSeats,
  );

  return (
    <section className="space-y-6">
      <h2 className="text-lg font-normal text-gray-900 dark:text-white">
        Register your team
      </h2>

      {/* Contact card */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base">Contact Person</CardTitle>
            <CardDescription>
              This person will receive all event communications.
            </CardDescription>
          </div>
          {!editingContact ? (
            <Button
              className="flex-shrink-0"
              onClick={() => {
                contactForm.reset({
                  contactName,
                  contactEmail,
                  contactPhone,
                });
                setEditingContact(true);
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          ) : (
            <div className="flex gap-1 flex-shrink-0">
              <Button
                onClick={handleSaveContact}
                size="sm"
                type="button"
                variant="ghost"
              >
                <Check className="h-3.5 w-3.5 mr-1.5" />
                Save
              </Button>
              <Button
                onClick={() => setEditingContact(false)}
                size="sm"
                type="button"
                variant="ghost"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {!editingContact ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Name
                </p>
                <p className="text-sm mt-1 text-gray-900 dark:text-white">
                  {contactName || (
                    <span className="text-muted-foreground italic">
                      Not set
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Email
                </p>
                <p className="text-sm mt-1 text-gray-900 dark:text-white">
                  {contactEmail || (
                    <span className="text-muted-foreground italic">
                      Not set
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Phone
                </p>
                <p className="text-sm mt-1 text-gray-900 dark:text-white">
                  {contactPhone || (
                    <span className="text-muted-foreground italic">
                      Not set
                    </span>
                  )}
                </p>
              </div>
            </div>
          ) : (
            <Form {...contactForm}>
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField
                    control={contactForm.control}
                    name="contactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Contact Name
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Full name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={contactForm.control}
                    name="contactEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Contact Email
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="email@company.com"
                            type="email"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={contactForm.control}
                    name="contactPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Phone
                        </FormLabel>
                        <FormControl>
                          <PhoneInput
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
                            country="US"
                            onChange={field.onChange}
                            placeholder="(123) 456-7890"
                            value={field.value}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>

      {/* Attendees card */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base">
              Attendees
              <span className="text-sm font-normal text-muted-foreground ml-2">
                {editingAttendees
                  ? `${selectedAttendeeCount} selected${editWaitlistedCount > 0 ? ` · ${editWaitlistedCount} waitlisted` : ""}`
                  : `${selectedAttendees.length} total${waitlistedCount > 0 ? ` · ${waitlistedCount} waitlisted` : ""}`}
              </span>
            </CardTitle>
            <CardDescription>
              {editingAttendees
                ? "Search employees or add attendees manually."
                : "Review your attendees and add any special requests."}
            </CardDescription>
          </div>
          {!editingAttendees ? (
            <Button
              className="flex-shrink-0"
              onClick={() => {
                attendeesForm.reset({
                  attendees: attendees.map((a) => ({
                    firstName: a.firstName,
                    lastName: a.lastName,
                    email: a.email,
                    isSelected: a.isSelected,
                    specialRequests: a.specialRequests,
                  })),
                });
                setEditingAttendees(true);
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          ) : (
            <div className="flex gap-1 flex-shrink-0">
              <Button
                onClick={handleSaveAttendees}
                size="sm"
                type="button"
                variant="ghost"
              >
                <Check className="h-3.5 w-3.5 mr-1.5" />
                Done
              </Button>
              <Button
                onClick={() => setEditingAttendees(false)}
                size="sm"
                type="button"
                variant="ghost"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {!editingAttendees ? (
            // Display mode
            <div>
              {selectedAttendees.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  No attendees added yet.{" "}
                  <button
                    className="text-foreground underline underline-offset-2"
                    onClick={() => setEditingAttendees(true)}
                    type="button"
                  >
                    Add attendees
                  </button>
                </p>
              ) : (
                selectedAttendees.map((a, i) => {
                  const globalIndex = attendees.indexOf(a);
                  const isExpanded = expandedRequests.has(globalIndex);
                  const requestValue = specialRequests[globalIndex] || "";
                  return (
                    <div
                      className="py-3 border-b border-border last:border-b-0"
                      key={globalIndex}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {a.firstName} {a.lastName}
                        </span>
                        {a.email && (
                          <span className="text-xs text-muted-foreground">
                            {a.email}
                          </span>
                        )}
                        {i >= availableSeats && (
                          <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                            Waitlist
                          </span>
                        )}
                      </div>
                      {!isExpanded && requestValue && (
                        <div className="mt-1 flex items-start gap-1.5">
                          <p className="text-xs text-muted-foreground italic flex-1">
                            {requestValue}
                          </p>
                          <button
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                            onClick={() => toggleSpecialRequests(globalIndex)}
                            type="button"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                      {!isExpanded && !requestValue && (
                        <button
                          className="mt-1 text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                          onClick={() => toggleSpecialRequests(globalIndex)}
                          type="button"
                        >
                          <Plus className="h-3 w-3" />
                          Add special requests
                        </button>
                      )}
                      {isExpanded && (
                        <div className="mt-2">
                          <Textarea
                            className="resize-none"
                            onChange={(e) =>
                              handleSpecialRequestChange(
                                globalIndex,
                                e.target.value,
                              )
                            }
                            placeholder="Dietary restrictions, accessibility needs, etc."
                            rows={2}
                            value={requestValue}
                          />
                          <button
                            className="mt-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                            onClick={() => toggleSpecialRequests(globalIndex)}
                            type="button"
                          >
                            Done
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            // Edit mode — inline attendee management
            <FormProvider {...attendeesForm}>
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="space-y-4">
                  {/* Existing attendees */}
                  {fields.length > 0 && (
                    <div>
                      {fields.map((field, index) => {
                        let isWaitlisted = false;
                        if (availableSeats >= 0) {
                          let selectedSoFar = 0;
                          for (let i = 0; i <= index; i++) {
                            if (watchedAttendees[i]?.isSelected) {
                              selectedSoFar++;
                            }
                          }
                          isWaitlisted =
                            !!watchedAttendees[index]?.isSelected &&
                            selectedSoFar > availableSeats;
                        }

                        const attendeeValues = watchedAttendees[index];
                        const displayName = [
                          attendeeValues?.firstName,
                          attendeeValues?.lastName,
                        ]
                          .filter(Boolean)
                          .join(" ");

                        return (
                          <div
                            className="py-2.5 border-b border-border last:border-b-0"
                            key={field.id}
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                  <span className="text-sm font-medium truncate text-gray-900 dark:text-white">
                                    {displayName || "Unnamed attendee"}
                                  </span>
                                  {attendeeValues?.email && (
                                    <span className="text-xs text-muted-foreground truncate">
                                      {attendeeValues.email}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span
                                className={`text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0 ${
                                  isWaitlisted
                                    ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                    : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                }`}
                              >
                                {isWaitlisted ? "Waitlist" : "Confirmed"}
                              </span>
                              <button
                                aria-label="Remove attendee"
                                className="p-1 rounded text-muted-foreground/30 hover:text-destructive transition-colors flex-shrink-0"
                                onClick={() => remove(index)}
                                type="button"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {fields.length > 0 && (
                    <div className="border-t border-border" />
                  )}

                  {/* Add attendees */}
                  <AddAttendees
                    duplicateEmailError={duplicateEmailError}
                    onAddAttendee={handleAddAttendee}
                    onClearDuplicateEmailError={() =>
                      setDuplicateEmailError(null)
                    }
                  />
                </div>
              </form>
            </FormProvider>
          )}
        </CardContent>
      </Card>

      {/* Group notes card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Group Notes
            <span className="text-sm font-normal text-muted-foreground ml-2">
              optional
            </span>
          </CardTitle>
          <CardDescription>
            Anything the organizer should know about your group.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            className="resize-none"
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. We'll arrive 15 minutes early for setup"
            rows={3}
            value={notes}
          />
        </CardContent>
      </Card>

      {/* Price summary + continue */}
      <div className="space-y-4">
        <PriceSummary
          attendeeCount={confirmedCount + waitlistedCount}
          isCoveredByTotalAccess={isCoveredByTotalAccess}
          price={eventPrice}
          waitlistedCount={waitlistedCount}
        />
        <Button
          className="w-full"
          disabled={editingContact || editingAttendees}
          onClick={handleContinue}
          type="button"
        >
          Continue to Payment
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </section>
  );
}
